import React from "react";
import { Calendar, User, CreditCard, Euro, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Pencil, Trash2, UserCircle, RefreshCw, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDateEU } from "@/utils/dateFormat";

const SUBSCRIPTION_PAYMENT_LABELS = {
  CARD: "Bancontact / myPOS",
  QR_BANCONTACT: "QR code Bancontact",
  TRANSFER: "Virement / Facture",
  CASH: "Cash / Numéraire",
  OTHER: "FloaPay / Autre",
};

const MANUAL_PAYMENT_LABELS = {
  CASH: "Numéraire",
  BANCONTACT: "Bancontact (Payconiq)",
  QR_BANCONTACT: "QR code Bancontact",
  MYPOS: "Boîtier MyPos",
  TRANSFER: "Virement bancaire",
  INVOICE: "Facture à prévoir",
  OTHER: "Autre",
};

const paymentLabel = (type, entryType) =>
  (entryType === "manual" ? MANUAL_PAYMENT_LABELS : SUBSCRIPTION_PAYMENT_LABELS)[type] || type;

const BADGE_COLORS = {
  CARD: "bg-blue-100 text-blue-700",
  TRANSFER: "bg-purple-100 text-purple-700",
  CASH: "bg-green-100 text-green-700",
  BANCONTACT: "bg-blue-100 text-blue-700",
  MYPOS: "bg-indigo-100 text-indigo-700",
  INVOICE: "bg-orange-100 text-orange-700",
};

const SortableHeader = ({ label, sortKey, currentSort, onSort, icon: Icon }) => {
  const isActive = currentSort.by === sortKey;
  return (
    <th
      className="text-left px-4 py-3 text-xs font-semibold text-[#577083] uppercase tracking-wide cursor-pointer hover:bg-[#edf0f4] transition-colors select-none"
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <div className="flex items-center gap-1">
        {Icon && <Icon size={14} />}
        {label}
        <span className="ml-1">
          {isActive && currentSort.order === "asc"  ? <ArrowUp size={14} className="text-[#3899ec]" /> :
           isActive && currentSort.order === "desc" ? <ArrowDown size={14} className="text-[#3899ec]" /> :
           <ArrowUpDown size={14} className="text-[#c1c9d0]" />}
        </span>
      </div>
    </th>
  );
};

const PaymentRow = ({ payment, index, isLast, lastPaymentRef, onEdit, onDelete, onViewMember }) => {
  const isManual = payment.entry_type === "manual";
  return (
    <tr
      ref={isLast ? lastPaymentRef : null}
      className={`border-t border-[#edf0f4] hover:bg-[#f9fafb] transition-colors ${isManual ? "bg-[#fef9c3]/30" : ""}`}
      data-testid={`payment-row-${index}`}
    >
      <td className="px-4 py-3 text-[#162d3d] font-medium">
        <div className="flex items-center gap-2 flex-wrap">
          {formatDateEU(payment.payment_date)}
          {isManual && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#f59e0b]/20 text-[#b45309]">Manuel</span>
          )}
          {isManual && payment.date_modified && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700" title={`Date modifiée par ${payment.date_modified_by || "inconnu"}`}>
              Date modifiée
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-[#162d3d] font-medium">{payment.last_name}</td>
      <td className="px-4 py-3 text-[#577083]">{payment.first_name}</td>
      <td className="px-4 py-3 text-[#577083]">
        {isManual ? (
          <div>
            <span className="text-[#162d3d]" title={payment.nature}>
              {payment.nature?.length > 30 ? payment.nature.substring(0, 30) + "..." : payment.nature}
            </span>
            {payment.coach_name && (
              <div className="flex items-center gap-1 text-xs text-[#8fa4b5] mt-0.5">
                <UserCircle size={12} /> {payment.coach_name}
              </div>
            )}
          </div>
        ) : (
          <span className="inline-block max-w-[200px] truncate" title={payment.plan_name}>{payment.plan_name}</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`crm-badge text-xs ${BADGE_COLORS[payment.payment_type] || "bg-gray-100 text-gray-700"}`}>
          {paymentLabel(payment.payment_type, payment.entry_type)}
        </span>
      </td>
      <td className="px-4 py-3 text-right font-semibold text-[#22c55e]">
        {payment.price_eur.toLocaleString("fr-BE", { minimumFractionDigits: 2 })} €
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1">
          {isManual ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => onEdit(payment)} className="text-[#3899ec] hover:text-[#3899ec]/80" data-testid={`edit-payment-${index}`}>
                <Pencil size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(payment)} className="text-red-500 hover:text-red-600" data-testid={`delete-payment-${index}`}>
                <Trash2 size={16} />
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => onViewMember(payment.member_id)} className="text-[#3899ec] hover:text-[#3899ec]/80" data-testid={`view-member-${index}`}>
              <ExternalLink size={16} />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export const CashbookTable = ({ payments, loading, loadingMore, hasMore, total, sort, onSort, onEdit, onDelete, onViewMember, lastPaymentRef }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#edf0f4] overflow-hidden">
        <div className="p-8 text-center text-[#8fa4b5]" data-testid="loading-indicator">
          <RefreshCw size={32} className="animate-spin mx-auto mb-2" />
          Chargement du livre de caisse...
        </div>
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-[#edf0f4] overflow-hidden">
        <div className="p-8 text-center text-[#8fa4b5]" data-testid="empty-state">
          <BookOpen size={48} className="mx-auto mb-3 text-[#c1c9d0]" />
          <p className="text-lg font-medium">Aucun paiement trouvé</p>
          <p className="text-sm">Les paiements apparaîtront ici une fois enregistrés.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[#edf0f4] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cashbook-table">
          <thead className="bg-[#f4f7fa] sticky top-0 z-10">
            <tr>
              <SortableHeader label="Date" sortKey="date" currentSort={sort} onSort={onSort} icon={Calendar} />
              <SortableHeader label="Nom" sortKey="last_name" currentSort={sort} onSort={onSort} icon={User} />
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#577083] uppercase tracking-wide">Prénom</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#577083] uppercase tracking-wide">Détail / Nature</th>
              <SortableHeader label="Mode de paiement" sortKey="payment_type" currentSort={sort} onSort={onSort} icon={CreditCard} />
              <SortableHeader label="Montant" sortKey="price_eur" currentSort={sort} onSort={onSort} icon={Euro} />
              <th className="text-center px-4 py-3 text-xs font-semibold text-[#577083] uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment, index) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                index={index}
                isLast={index === payments.length - 1}
                lastPaymentRef={lastPaymentRef}
                onEdit={onEdit}
                onDelete={onDelete}
                onViewMember={onViewMember}
              />
            ))}
          </tbody>
        </table>
        {loadingMore && (
          <div className="p-4 text-center text-[#8fa4b5] border-t border-[#edf0f4]" data-testid="loading-more">
            <RefreshCw size={20} className="animate-spin inline mr-2" /> Chargement...
          </div>
        )}
        {!hasMore && payments.length > 0 && (
          <div className="p-4 text-center text-[#8fa4b5] border-t border-[#edf0f4] text-sm" data-testid="end-of-list">
            — Fin de la liste ({total} paiements) —
          </div>
        )}
      </div>
    </div>
  );
};

export { MANUAL_PAYMENT_LABELS };
